import { Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import api from './api';
import { SimRechargeJob, StockCarteSim, StatutStock } from '../types';

const NUMEROS_FILE_NAME = 'numero.txt';

const stockCarteSimService = {
  getAll: () =>
    api.get<StockCarteSim[]>('/api/stock/sims').then((r) => r.data),

  create: (data: { numero: string; modele?: string }) =>
    api.post<StockCarteSim>('/api/stock/sims', data).then((r) => r.data),

  update: (id: number, data: { statut?: StatutStock; modele?: string; numero?: string }) =>
    api.put<StockCarteSim>(`/api/stock/sims/${id}`, data).then((r) => r.data),

  delete: (id: number) =>
    api.delete(`/api/stock/sims/${id}`).then((r) => r.data),

  recharger: (ids: number[], dateRecharge?: string) =>
    api.post<StockCarteSim[]>('/api/stock/sims/recharge', { ids, dateRecharge }).then((r) => r.data),

  createRechargeJob: (simIds: number[]) =>
    api.post<SimRechargeJob>('/api/stock/sims/recharge-jobs', { simIds }).then((r) => r.data),

  getRechargeJob: (jobId: number) =>
    api.get<SimRechargeJob>(`/api/stock/sims/recharge-jobs/${jobId}`).then((r) => r.data),

  stopRechargeJob: (jobId: number) =>
    api.post<SimRechargeJob>(`/api/stock/sims/recharge-jobs/${jobId}/stop`).then((r) => r.data),

  downloadRechargeReport: async (jobId: number) => {
    const fileName = `recharge-sim-${jobId}.csv`;
    const response = await api.get<string>(`/api/stock/sims/recharge-jobs/${jobId}/report.csv`, {
      responseType: 'text',
    });
    const content = response.data ?? '';

    if (Platform.OS === 'web') {
      const webGlobal = globalThis as unknown as {
        URL?: {
          createObjectURL: (blob: unknown) => string;
          revokeObjectURL: (url: string) => void;
        };
        document?: {
          createElement: (tagName: 'a') => {
            href: string;
            setAttribute: (name: string, value: string) => void;
            click: () => void;
            remove: () => void;
          };
          body: { appendChild: (node: unknown) => void };
        };
        Blob?: new (parts: string[], options: { type: string }) => unknown;
      };
      const urlApi = webGlobal.URL;
      const doc = webGlobal.document;
      const BlobCtor = webGlobal.Blob;
      if (!urlApi || !doc || !BlobCtor) {
        throw new Error('Téléchargement non supporté sur cet environnement web.');
      }

      const url = urlApi.createObjectURL(new BlobCtor([content], { type: 'text/csv;charset=utf-8' }));
      const link = doc.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      doc.body.appendChild(link);
      link.click();
      link.remove();
      urlApi.revokeObjectURL(url);
      return;
    }

    const directory = FileSystem.documentDirectory ?? FileSystem.cacheDirectory;
    if (!directory) {
      throw new Error('Espace de stockage indisponible sur cet appareil.');
    }

    const fileUri = `${directory}${fileName}`;
    await FileSystem.writeAsStringAsync(fileUri, content, {
      encoding: FileSystem.EncodingType.UTF8,
    });

    if (await Sharing.isAvailableAsync()) {
      await Sharing.shareAsync(fileUri, {
        mimeType: 'text/csv',
        dialogTitle: `Enregistrer ${fileName}`,
        UTI: 'public.comma-separated-values-text',
      });
    }
  },

  downloadNumeros: async (numeros: string[], fileName = NUMEROS_FILE_NAME) => {
    const content = `${numeros.join('\n')}\n`;

    if (Platform.OS === 'web') {
      const webGlobal = globalThis as unknown as {
        URL?: {
          createObjectURL: (blob: unknown) => string;
          revokeObjectURL: (url: string) => void;
        };
        document?: {
          createElement: (tagName: 'a') => {
            href: string;
            setAttribute: (name: string, value: string) => void;
            click: () => void;
            remove: () => void;
          };
          body: { appendChild: (node: unknown) => void };
        };
        Blob?: new (parts: string[], options: { type: string }) => unknown;
      };
      const urlApi = webGlobal.URL;
      const doc = webGlobal.document;
      const BlobCtor = webGlobal.Blob;
      if (!urlApi || !doc || !BlobCtor) {
        throw new Error('Téléchargement non supporté sur cet environnement web.');
      }

      const url = urlApi.createObjectURL(new BlobCtor([content], { type: 'text/plain;charset=utf-8' }));
      const link = doc.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      doc.body.appendChild(link);
      link.click();
      link.remove();
      urlApi.revokeObjectURL(url);
      return;
    }

    const directory = FileSystem.documentDirectory ?? FileSystem.cacheDirectory;
    if (!directory) {
      throw new Error('Espace de stockage indisponible sur cet appareil.');
    }

    const fileUri = `${directory}${fileName}`;
    await FileSystem.writeAsStringAsync(fileUri, content, {
      encoding: FileSystem.EncodingType.UTF8,
    });

    if (await Sharing.isAvailableAsync()) {
      await Sharing.shareAsync(fileUri, {
        mimeType: 'text/plain',
        dialogTitle: `Enregistrer ${fileName}`,
        UTI: 'public.plain-text',
      });
    }
  },
};

export default stockCarteSimService;
